/**
 * HomeScreen — 3 variants: idle, scheduled, active.
 * UI-INV-005: Leaf component, props only, no engine imports.
 * Task 08 (Session E) · redesign: monastery entrance, History-aligned vocabulary.
 */
import { useState, useEffect } from "react";
import styles from "./HomeScreen.module.css";
import { TEST_IDS, HOME_WAKE_OFFSET_ID } from "../testing/testIds";
import { IntentStrip } from "../components/IntentStrip";
import { FeedbackBanner } from "../components/FeedbackBanner";
import { TIMING } from "../constants";
import type { WakeOffset } from "../constants";
import { formatTime12h } from "../utils/scheduleUtils";
import type { TraditionRow } from "../content/traditions";

// ── Countdown helpers ─────────────────────────────────────────────────────────

function formatCountdown(ms: number): string {
  if (ms <= 0) return "The window passed · sit now?";
  if (ms < 60_000) return "now";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m from now` : `${hours}h from now`;
}


function formatScheduledDate(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function toSoftTimeLabel(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const hour = d.getHours();

  let dayPart: string;
  if (d.toDateString() === today.toDateString()) dayPart = "today";
  else if (d.toDateString() === tomorrow.toDateString()) dayPart = "tomorrow";
  else dayPart = d.toLocaleDateString("en-US", { weekday: "long" });

  let timePart: string;
  if (hour >= 4 && hour < 8) timePart = "before the room lightens";
  else if (hour >= 8 && hour < 12) timePart = "in the morning";
  else if (hour >= 12 && hour < 14) timePart = "at midday";
  else if (hour >= 14 && hour < 18) timePart = "in the afternoon";
  else if (hour >= 18 && hour < 21) timePart = "in the evening";
  else timePart = "at night";

  return `${dayPart} · ${timePart}`;
}

function formatLastSat(ms: number): string {
  const days = Math.floor((Date.now() - ms) / TIMING.DAY_MS);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return new Date(ms).toLocaleDateString("en-US", { month: "long", day: "numeric" });
}

// ── Props ──────────────────────────────────────────────────────────────────────

type NowSession = {
  name: string;
  term: string;
  remainingSeconds: number;
  durationSeconds: number;
};

type NextSession = {
  name: string;
  term: string;
  atLabel: string;
};

type SharedProps = {
  intentLabels: string[];
  todayLabel: string;
  whisperLine: string;
  /** Attribution line, e.g. "— The Buddha". Rendered below the whisper in small type. */
  whisperAttribution?: string;
  onOpenSettings: () => void;
  /** Non-null for wave0/beta users — drives the feedback banner. */
  wave?: string | null;
  /** Opens the feedback form. */
  onOpenFeedback?: () => void;
};

type IdleProps = {
  variant: "idle";
  onFindPractice: () => void;
  /** Called with tradition ID ("zen" | "vipassana" | "secular") when a door is tapped. */
  onSelectTradition?: (id: string) => void;
  onQuickStart?: () => void;
  /** Returning-practitioner acknowledgment — renders only when completedCount > 0. */
  completedCount?: number;
  lastSessionDate?: number | null;
  /** Tradition door rows — provided by the bridge from the content package. */
  traditions?: TraditionRow[];
  /** Called when the user taps "Install app". Only provided when not in standalone mode and app not yet installed. */
  onInstall?: () => void;
  /** Called when the user taps "Reload to update". Only provided when a new SW version is waiting. */
  onUpdateReady?: () => void;
};

type ScheduledProps = {
  variant: "scheduled";
  scheduledRetreatName: string;
  scheduledAt: number | null;
  wakeOffset: WakeOffset;
  onChangeWakeOffset: (v: WakeOffset) => void;
  onOpenTrust: () => void;
  onBeginNow: () => void;
  onCancelScheduled: () => void;
  requirements?: string;
};

type ActiveProps = {
  variant: "active";
  retreatName: string;
  dayLabel: string;
  nowSession?: NowSession;
  nextSession?: NextSession;
  retreatEndsLabel?: string;
  onReturnToPractice: () => void;
  onEndRetreat: () => void;
};

export type HomeScreenProps = SharedProps & (IdleProps | ScheduledProps | ActiveProps);

// ── Settings button ───────────────────────────────────────────────────────────

function SettingsGear({ onClick }: { onClick: () => void }) {
  return (
    <button className={styles.settingsBtn} onClick={onClick} aria-label="Settings" type="button" data-testid={TEST_IDS.HOME_SETTINGS_BTN}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    </button>
  );
}

// ── WakeOffset picker options ─────────────────────────────────────────────────

const WAKE_OFFSET_OPTIONS: Array<{ value: WakeOffset; label: string }> = [
  { value: "30min", label: "30 min before" },
  { value: "15min", label: "15 min" },
  { value: "5min",  label: "5 min" },
  { value: "self",  label: "I'll wake on my own" },
];

// ── Shared sub-elements ───────────────────────────────────────────────────────
// Tab navigation is rendered externally by RetreatBridge, not by HomeScreen.
// HomeScreen is a leaf component (UI-INV-005); it does not own shell chrome.

function topBarFor(p: SharedProps) {
  return (
    <div className={styles.topBar}>
      <span className={styles.topDate}>{p.todayLabel}</span>
      <SettingsGear onClick={p.onOpenSettings} />
    </div>
  );
}

// ── IDLE variant ──────────────────────────────────────────────────────────────

function IdleHome(props: SharedProps & IdleProps) {
  const { completedCount = 0, lastSessionDate, traditions = [] } = props;
  const lastSat = lastSessionDate ? formatLastSat(lastSessionDate) : null;

  return (
    <div className={styles.home} data-screen-label="Home · idle" data-testid={TEST_IDS.HOME_SCREEN}>
      {topBarFor(props)}
      <div className={styles.content}>
        <div className={styles.idleGrid}>
          {/* Left column on desktop: whisper + context */}
          <div className={styles.idleLeft}>
            {props.intentLabels.length > 0 && (
              <div className={styles.intentWrap}>
                <IntentStrip intents={props.intentLabels} />
              </div>
            )}

            {completedCount > 0 && (
              <p className={styles.returningLine}>
                {completedCount} sitting{completedCount === 1 ? "" : "s"} kept
                {lastSat && <span className={styles.returningDot}> · </span>}
                {lastSat && <span>last sat {lastSat}</span>}
              </p>
            )}

            <div className={styles.entranceMark} aria-hidden="true" />
            <p className={styles.heroWhisper} data-testid={TEST_IDS.WHISPER_LINE}>{props.whisperLine}</p>
            {props.whisperAttribution && (
              <p className={styles.whisperAttribution}>{props.whisperAttribution}</p>
            )}
          </div>

          {/* Right column on desktop: CTA + lineage doors */}
          <div className={styles.idleRight}>
            <button
              className={styles.gate}
              onClick={props.onFindPractice}
              type="button"
              data-testid={TEST_IDS.BROWSE_ALL_CTA}
            >
              <span className={styles.gateArch} aria-hidden="true" />
              <span className={styles.gateInner}>
                <span className={styles.gateLabel}>Find a practice</span>
                <span className={styles.gateSub}>step in · browse every retreat</span>
                <span className={styles.gateArrow} aria-hidden="true">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5v14M6 13l6 6 6-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </span>
            </button>

            {props.onQuickStart && (
              <button
                className={styles.quickStart}
                onClick={props.onQuickStart}
                type="button"
                data-testid={TEST_IDS.QUICK_START_DEMO}
              >
                <span>Quick start (demo)</span>
              </button>
            )}

            {props.onInstall && (
              <button
                className={styles.installBtn}
                onClick={props.onInstall}
                type="button"
                data-testid={TEST_IDS.HOME_INSTALL_BTN}
              >
                Install app
              </button>
            )}

            {props.onUpdateReady && (
              <div
                className={styles.updateBanner}
                data-testid={TEST_IDS.SW_UPDATE_BANNER}
              >
                <span>A new version is available.</span>
                <button
                  className={styles.updateBtn}
                  onClick={props.onUpdateReady}
                  type="button"
                  data-testid={TEST_IDS.SW_UPDATE_RELOAD_BTN}
                >
                  Reload to update
                </button>
              </div>
            )}

            {completedCount === 0 && (
              <div className={styles.firstTimerCard}>
                <span className={styles.firstTimerLabel}>New to retreats?</span>
                <button
                  className={styles.firstTimerBtn}
                  onClick={() => props.onSelectTradition ? props.onSelectTradition("zen") : props.onFindPractice()}
                  type="button"
                >
                  Start with the 2-hour Zen retreat →
                </button>
              </div>
            )}

            <p className={styles.doorsEyebrow}>Or enter by lineage</p>
            <div className={styles.doorList}>
              {traditions.map((t) => (
                <button
                  key={t.id}
                  className={styles.door}
                  onClick={() => props.onSelectTradition ? props.onSelectTradition(t.id) : props.onFindPractice()}
                  type="button"
                >
                  <span className={styles.doorSeal} style={{ ["--seal" as string]: t.seal } as React.CSSProperties}>
                    <span className={styles.doorSealDot} />
                  </span>
                  <span className={styles.doorBody}>
                    <span className={styles.doorTopline}>
                      <span className={styles.doorName}>{t.name}</span>
                      <span className={styles.doorTerm}>{t.term}</span>
                    </span>
                    <span className={styles.doorLine}>{t.line}</span>
                  </span>
                  <span className={styles.doorEnter}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
              ))}
            </div>
            <p className={styles.doorsFoot}>Chanting, Sufi and more — after the beta.</p>
            <FeedbackBanner
              isBeta={Boolean(props.wave)}
              onOpenFeedback={props.onOpenFeedback}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SCHEDULED variant ─────────────────────────────────────────────────────────

function ScheduledHome(props: SharedProps & ScheduledProps) {
  const { scheduledRetreatName, scheduledAt, wakeOffset, onChangeWakeOffset, onOpenTrust, onBeginNow, onCancelScheduled, requirements } = props;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TIMING.SCHEDULED_HOME_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const msUntil = scheduledAt ? scheduledAt - now : 0;
  const isMissed = scheduledAt != null && msUntil < 0;
  const countdown = scheduledAt ? formatCountdown(msUntil) : "—";
  const preciseTime = scheduledAt ? formatTime12h(scheduledAt) : "—";
  const dateLabel = scheduledAt ? formatScheduledDate(scheduledAt) : "";
  const softLabel = scheduledAt ? toSoftTimeLabel(scheduledAt) : "";

  return (
    <div className={styles.home} data-screen-label="Home · scheduled" data-testid={TEST_IDS.HOME_SCREEN}>
      {topBarFor(props)}
      <div className={styles.content}>
        {props.intentLabels.length > 0 && (
          <div className={styles.intentWrap}>
            <IntentStrip intents={props.intentLabels} />
          </div>
        )}

        <p className={`${styles.eyebrow} ${styles.eyebrowClay}`}>Scheduled</p>
        <h1 className={styles.heading}>{scheduledRetreatName}</h1>

        <div className={styles.scheduleHero}>
          {isMissed ? (
            <p className={styles.missed}>The window passed · sit now?</p>
          ) : (
            <>
              <p className={styles.softTime}>{softLabel}</p>
              <p className={styles.preciseTime}>{preciseTime}</p>
              <p className={styles.countdown}>{dateLabel} · {countdown}</p>
            </>
          )}
        </div>

        {props.whisperLine && (
          <>
            <p className={styles.whisper}>{props.whisperLine}</p>
            {props.whisperAttribution && (
              <p className={styles.whisperAttribution}>{props.whisperAttribution}</p>
            )}
          </>
        )}

        {requirements && (
          <div className={styles.card}>
            <p className={styles.cardEyebrow}>Before you begin</p>
            <p className={styles.requirementsText}>{requirements}</p>
          </div>
        )}

        <div className={`${styles.card} ${styles.alarmCard}`}>
          <p className={styles.cardEyebrow}>Wake-up alarm</p>
          <div className={styles.offsetPills}>
            {WAKE_OFFSET_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                className={`${styles.offsetPill} ${wakeOffset === opt.value ? styles.offsetPillActive : ""}`}
                onClick={() => onChangeWakeOffset?.(opt.value)}
                type="button"
                data-testid={HOME_WAKE_OFFSET_ID(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <button
          className={styles.beginNowBtn}
          onClick={onBeginNow}
          type="button"
          data-testid={TEST_IDS.HOME_SCHEDULED_BEGIN_BTN}
        >
          Begin now
        </button>

        <div className={styles.scheduledFooter}>
          <button
            className={styles.trustLink}
            onClick={onOpenTrust}
            type="button"
            data-testid={TEST_IDS.HOME_TRUST_BADGE}
          >
            <span className={styles.trustCheck} aria-hidden="true">✓</span> The bell will ring
          </button>
          <span className={styles.footerDot}>·</span>
          <button
            className={styles.cancelLink}
            onClick={onCancelScheduled}
            type="button"
            data-testid={TEST_IDS.HOME_SCHEDULED_CANCEL_BTN}
          >
            Cancel this retreat
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ACTIVE variant ────────────────────────────────────────────────────────────

function ActiveHome(props: SharedProps & ActiveProps) {
  const { retreatName, dayLabel, nowSession, nextSession, retreatEndsLabel, onReturnToPractice, onEndRetreat } = props;

  const progressPct =
    nowSession && nowSession.durationSeconds > 0
      ? Math.min(100, (1 - nowSession.remainingSeconds / nowSession.durationSeconds) * 100)
      : 0;

  const remainingLabel =
    nowSession && nowSession.remainingSeconds > 0
      ? `${Math.ceil(nowSession.remainingSeconds / 60)} min remain`
      : "—";

  return (
    <div className={styles.home} data-screen-label="Home · active" data-testid={TEST_IDS.HOME_SCREEN}>
      {topBarFor(props)}
      <div className={styles.content} data-testid={TEST_IDS.HOME_RESUME_CARD}>
        {props.intentLabels.length > 0 && (
          <div className={styles.intentWrap}>
            <IntentStrip intents={props.intentLabels} />
          </div>
        )}

        <p className={`${styles.eyebrow} ${styles.eyebrowClay}`}>In retreat · {dayLabel}</p>
        <h1 className={styles.heading}>{retreatName}</h1>
        {props.whisperLine && (
          <>
            <p className={styles.whisper}>{props.whisperLine}</p>
            {props.whisperAttribution && (
              <p className={styles.whisperAttribution}>{props.whisperAttribution}</p>
            )}
          </>
        )}

        {nowSession && (
          <div className={`${styles.card} ${styles.nowCard}`}>
            <div className={styles.cardTop}>
              <p className={styles.cardEyebrow}>Now</p>
              <span className={styles.remainingMono}>{remainingLabel}</span>
            </div>
            <p className={styles.sessionName}>{nowSession.name}</p>
            <p className={styles.sessionTerm}>{nowSession.term}</p>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {nextSession && (
          <div className={styles.card}>
            <div className={styles.cardTop}>
              <p className={styles.cardEyebrow}>Next</p>
              <span className={styles.atMono}>at {nextSession.atLabel}</span>
            </div>
            <p className={styles.sessionName}>{nextSession.name}</p>
            <p className={styles.sessionTerm}>{nextSession.term}</p>
          </div>
        )}

        <button
          className={styles.returnCta}
          onClick={onReturnToPractice}
          type="button"
          data-testid={TEST_IDS.HOME_RESUME_BTN}
        >
          Return to practice
        </button>

        <button className={styles.endLink} onClick={onEndRetreat} type="button" data-testid={TEST_IDS.HOME_RESUME_END_BTN}>
          <span className={styles.endLinkUnderline}>End the retreat</span> · saves what you've done
        </button>

        {retreatEndsLabel && (
          <p className={styles.retreatEndsFooter}>{retreatEndsLabel}</p>
        )}
      </div>
    </div>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

export function HomeScreen(props: HomeScreenProps) {
  if (props.variant === "idle") return <IdleHome {...props} />;
  if (props.variant === "scheduled") return <ScheduledHome {...props} />;
  return <ActiveHome {...props} />;
}
